import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader, type LoaderProps } from '@/components/Loader';
import { useCropSrcset } from '@/hooks/useCropSrcset';

import * as S from './CropImage.styles';
export interface CropImageProps {
  alt: string;
  coords: { x1: number; x2: number; y1: number; y2: number };
  height: number;
  loaderProps?: LoaderProps;
  loading: 'eager' | 'lazy' | undefined;
  mobileWidth?: number | string;
  originalImageDimensions?: { x: number; y: number };
  showFallbackPlaceholder?: boolean;
  sizes: string;
  src: string;
  useLoader: boolean;
  width: number | string;
  widths?: number[];
}

export const CropImage: React.FC<CropImageProps> = (props) => {
  const {
    alt,
    coords,
    loaderProps,
    loading,
    mobileWidth,
    showFallbackPlaceholder,
    sizes,
    src,
    useLoader,
    width,
  } = props;
  let { widths } = props;
  widths = widths ?? [192, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840];

  const { newSrc, srcSet } = useCropSrcset(
    widths,
    src,
    {
      height: props.originalImageDimensions?.y ?? 0,
      width: props.originalImageDimensions?.x ?? 0,
    },
    coords,
  );

  const [loadingState, setLoadingState] = useState<
    'loading' | 'error' | 'loaded'
  >('loading');
  const [retryCount, setRetryCount] = useState(0);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loadingState === 'loading') {
        setLoadingState('error');
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [newSrc, loadingState]);

  useEffect(() => {
    const img = new Image();
    img.src = newSrc;
    img.onload = () => setLoadingState('loaded');
  }, [newSrc]);

  const handleImageError = () => {
    if (retryCount < 5) {
      setTimeout(() => {
        setRetryCount(retryCount + 1);
      }, 1000);
    } else {
      setLoadingState('error');
    }
  };

  const StyledImage = styled.img<{
    loadingState?: string;
    mobileWidth?: number | string;
  }>`
    visibility: ${({ loadingState }) =>
      loadingState === 'loaded' ? 'visible' : 'hidden'};
    @media (max-width: 480px) {
      width: ${({ mobileWidth }) =>
        mobileWidth
          ? typeof mobileWidth === 'string'
            ? mobileWidth
            : `${mobileWidth}px`
          : 'auto'};
    }
  `;

  return (
    <S.CropImageDiv
      className={`crop-image_${loadingState}`}
      data-test-id='CropImage'
      mobileWidth={mobileWidth}
      width={width}
    >
      {useLoader && loadingState === 'loading' && (
        <Loader data-test-id='CropImageLoader' {...loaderProps} />
      )}
      {loadingState === 'error' && showFallbackPlaceholder && (
        <ImagePlaceholder data-test-id='CropImageFallbackPlaceholder' />
      )}
      <StyledImage
        alt={alt}
        data-test-id='CropImageStyledImage'
        loading={loading}
        loadingState={loadingState}
        mobileWidth={mobileWidth}
        onError={handleImageError}
        onLoad={() => setLoadingState('loaded')}
        sizes={sizes}
        src={newSrc}
        srcSet={srcSet}
        width={width}
      />
    </S.CropImageDiv>
  );
};

CropImage.displayName = 'CropImage';
